import { useEffect, useState } from "react";

export default function ListScreen({
  data,
  type,
  search,
  setSearch,
  onView,
  onEdit,
  onDel,
  onBulkDel,
  onBack,
  onExport,
  onToggleProcessing,
  onReopen,
  titleOverride,
  isAdmin,
  C,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingSaving, setProcessingSaving] = useState({});

  const RECEPTION_CHECKLIST_LABELS = {
    tollTag: "Bombrikke",
    rubberMats: "Gummimatter",
    fullTank: "Full tank/Fulladet",
    shelf: "Rollo/Hattehylle",
    cableType2: "Ladekabel type 2",
    emergencyCharger: "Ladekabel nødlader",
    twoTireSets: "Mottatt 2 dekksett",
    noDamage: "Fri for skader",
  };

  const RETURN_CHECKLIST_LABELS = {
    tollTag: "Bombrikke/belastet",
    spareKey: "Reservenøkkel",
    twoTireSets: "To dekksett",
    shelf: "Hattehylle/Rollo",
    emergencyCharger: "Nødlader",
    cableType2: "Ladekabel type 2",
    tank: "Full tank",
  };

  function getFailedChecklistTags(r) {
    const checklist = r.checklist;
    if (!checklist || typeof checklist !== "object") return [];
    const labels =
      type === "return" ? RETURN_CHECKLIST_LABELS : RECEPTION_CHECKLIST_LABELS;
    return Object.entries(checklist)
      .filter(([, val]) => val === false)
      .map(([key]) => labels[key] || key);
  }

  const title = titleOverride || (type === "return" ? "Returnerte biler" : "Mottatte biler");
  const visibleIds = data.map((r) => r.id);
  const visibleSelectedCount = selectedIds.filter((id) => visibleIds.includes(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => visibleIds.includes(id)));
  }, [visibleIds.join("|")]);

  const toggleVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleProcessing = async (record, field, value) => {
    if (!onToggleProcessing) return;
    const key = `${record.id}:${field}`;
    setProcessingSaving((prev) => ({ ...prev, [key]: true }));
    try {
      await onToggleProcessing(record, field, value);
    } finally {
      setProcessingSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const ids = [...selectedIds];
    await onBulkDel(ids);
    setSelectedIds([]);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <button onClick={onBack} className="btn-s">
          ← Tilbake
        </button>
        <h2 style={{ margin: 0, color: C.primary }}>{title}</h2>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <button onClick={onExport} className="btn-p">
            Eksporter
          </button>
          {isAdmin && selectedIds.length > 0 && (
            <button onClick={handleBulkDelete} className="btn-d">
              Slett valgte ({selectedIds.length})
            </button>
          )}
        </div>
      </div>
      <div style={{ marginBottom: "24px", maxWidth: "400px" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Søk regnr..."
          className="inp"
        />
      </div>
      {isAdmin && data.length > 0 && (
        <div
          className="card"
          style={{
            padding: "14px 18px",
            marginBottom: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }}>
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} />
            Velg alle synlige
          </label>
          <div style={{ color: C.darkGray, fontSize: "14px" }}>
            {visibleSelectedCount > 0 ? `${visibleSelectedCount} valgt` : "Ingen valgt"}
          </div>
        </div>
      )}
      {data.length === 0 ? (
        <div className="card" style={{ padding: "60px", textAlign: "center" }}>
          <p style={{ color: C.darkGray, margin: 0 }}>
            {search ? "Ingen treff" : "Ingen data"}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {data.map((r) => (
            <div
              key={r.id}
              className="card"
              style={{
                padding: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {isAdmin && (
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(r.id)}
                    onChange={() => toggleOne(r.id)}
                    style={{ width: "18px", height: "18px" }}
                  />
                )}
                <div
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `${C.accent}15`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                  }}
                >
                  🚗
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "18px",
                      color: C.primary,
                    }}
                  >
                    {r.basicInfo?.regNum || r.reg_num || "Ukjent"}
                  </div>
                  <div style={{ color: C.darkGray, fontSize: "14px" }}>
                    {type === "return"
                      ? `${r.basicInfo?.brand || ""} ${r.basicInfo?.model || ""}`.trim() || "-"
                      : `Mottatt: ${r.basicInfo?.date || "-"}`}
                  </div>
                </div>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {type === "return" ? (
                  <span className={r.focalx ? "status-ok" : "status-bad"}>
                    {r.focalx ? "Focalx ✓" : "Ingen Focalx"}
                  </span>
                ) : (
                  <span className={Array.isArray(r.damages) && r.damages.length ? "status-bad" : "status-ok"}>
                    {Array.isArray(r.damages) && r.damages.length ? `${r.damages.length} skade(r)` : "OK"}
                  </span>
                )}
                {type === "return" && (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!r.processing?.autoplanDone}
                        disabled={!!processingSaving[`${r.id}:autoplanDone`]}
                        onChange={(e) =>
                          handleToggleProcessing(r, "autoplanDone", e.target.checked)
                        }
                      />
                      Autoplan
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!r.processing?.autofleksDone}
                        disabled={!!processingSaving[`${r.id}:autofleksDone`]}
                        onChange={(e) =>
                          handleToggleProcessing(r, "autofleksDone", e.target.checked)
                        }
                      />
                      Autofleks
                    </label>
                  </div>
                )}
                {getFailedChecklistTags(r).map((label) => (
                  <span
                    key={label}
                    style={{
                      background: "rgba(255,71,87,0.15)",
                      color: "#ff4757",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      border: "1px solid rgba(255,71,87,0.3)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ✗ {label}
                  </span>
                ))}
                <span style={{ color: C.darkGray, fontSize: "14px" }}>
                  {new Date(r.created).toLocaleDateString("nb-NO")}
                </span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => onView(r)}
                  className="btn-s"
                  style={{ padding: "10px 16px", fontSize: "14px" }}
                >
                  Detaljer
                </button>
                <button
                  onClick={() => onEdit(r)}
                  className="btn-s"
                  style={{ padding: "10px 16px", fontSize: "14px" }}
                >
                  Rediger
                </button>
                {isAdmin && (
                  <>
                    {onReopen && (
                      <button
                        onClick={() => onReopen(r)}
                        className="btn-s"
                        style={{ padding: "10px 16px", fontSize: "14px" }}
                      >
                        Gjenåpne
                      </button>
                    )}
                  <button
                    onClick={() => onDel(r.id)}
                    className="btn-d"
                    style={{ padding: "10px 16px", fontSize: "14px" }}
                  >
                    Slett
                  </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
